import React from 'react'
import {
  dataAttr,
  isNumber,
  mergeClassnames,
  useCallbackRef,
  useMergeRefs
} from '@mote/helpers'
import { preventBubblingEvent } from '@mote/helpers/browser'
import { KeyboardKey, __DEV__ } from '@mote/helpers/constants'
import styled, { css, th } from '@xstyled/styled-components'

const SIZES = {
  sm: {
    switch: {
      height: 12,
      width: 22
    },
    font: 15
  },
  md: {
    switch: {
      height: 16,
      width: 30
    },
    font: 15
  },
  lg: {
    switch: {
      height: 20,
      width: 38
    },
    font: 15
  },
  xl: {
    switch: {
      height: 24,
      width: 46
    },
    font: 15
  }
}

export const Switch = React.forwardRef(
  (
    {
      checked,
      onChange: _onChange,
      onFocus: _onFocus,
      onBlur: _onBlur,
      className,
      color = 'primary.base',
      icon,
      iconOn,
      iconOff,
      name,
      label,
      value = '',
      size = 'md',
      gap = 8,
      labelPosition = 'right',
      disabled = false,
      invalid = false,
      defaultChecked = false,
      ...rest
    },
    ref
  ) => {
    const [selfChecked, setSelfChecked] = React.useState(defaultChecked)
    const [isFocused, setIsFocused] = React.useState(false)
    const [isActive, setIsActive] = React.useState(false)
    const [rootIsLabelElement, setRootIsLabelElement] = React.useState(true)
    const inputRef = React.useRef(null)

    const refs = useMergeRefs(ref, inputRef)

    const onChange = useCallbackRef(_onChange)
    const onFocus = useCallbackRef(_onFocus)
    const onBlur = useCallbackRef(_onBlur)

    if (icon && __DEV__ && (iconOn || iconOff)) {
      console.warn('Remove props "icon" if iconOn or iconOff exists.', 'Switch')
    }

    const rootRef = React.useCallback(node => {
      if (!node) return
      setRootIsLabelElement(node.tagName === 'LABEL')
    }, [])

    const onKeyDown = React.useCallback(event => {
      if (event.keyCode === KeyboardKey.SPACE) {
        setIsActive(true)
      }
    }, [])

    const onKeyUp = React.useCallback(event => {
      if (event.keyCode === KeyboardKey.SPACE) {
        setIsActive(false)
      }
    }, [])

    const onMouseDown = React.useCallback(event => {
      event.preventDefault()
      setIsActive(true)
    }, [])

    const onMouseUp = React.useCallback(() => {
      setIsActive(false)
    }, [])

    const handleRootClick = React.useCallback(() => {
      if (!rootIsLabelElement) {
        inputRef.current?.click()
        inputRef.current?.focus()
      }
    }, [rootIsLabelElement])

    const handleChange = React.useCallback(
      event => {
        if (disabled) return

        const selfEvent = {
          target: {
            checked: !selfChecked
          },
          stopPropagation: event.stopPropagation,
          preventDefault: event.preventDefault,
          nativeEvent: event
        }

        setSelfChecked(!selfChecked)
        onChange?.(selfEvent)
      },
      [disabled, selfChecked, onChange]
    )

    const handleFocus = React.useCallback(
      event => {
        setIsFocused(true)
        onFocus?.(event)
      },
      [onFocus]
    )

    const handleBlur = React.useCallback(
      event => {
        setIsFocused(false)
        setIsActive(false)
        onBlur?.(event)
      },
      [onBlur]
    )

    const circleIcon = React.useMemo(() => {
      const hasIcon = icon || iconOn || iconOff
      const hasIconOn = Boolean(iconOn)
      const hasIconOff = Boolean(iconOff)
      if (!hasIcon) return null
      if (hasIconOn && selfChecked) return iconOn
      if (hasIconOff && !selfChecked) return iconOff
      return hasIcon
    }, [selfChecked, icon, iconOn, iconOff])

    const labelElement = React.useMemo(() => {
      if (!label) return null

      const props = {
        size,
        onMouseDown: preventBubblingEvent,
        onTouchStart: preventBubblingEvent,
        'data-disabled': dataAttr(disabled),
        'data-checked': dataAttr(checked),
        'data-invalid': dataAttr(invalid)
      }

      return (
        <Label className='mote-switch__label' {...props}>
          {label}
        </Label>
      )
    }, [checked, disabled, invalid, label, size])

    React.useEffect(() => {
      if (checked === undefined) return
      setSelfChecked(checked)
    }, [checked])

    return (
      <Root
        ref={rootRef}
        className={mergeClassnames('mote-switch', className)}
        size={size}
        gap={gap}
        onClick={handleRootClick}
        data-disabled={dataAttr(disabled)}
        data-checked={dataAttr(selfChecked)}
        data-invalid={dataAttr(invalid)}
        data-active={dataAttr(isActive)}
      >
        <SwitchInput
          ref={refs}
          as='input'
          type='checkbox'
          name={name}
          className='mote-switch__input'
          value={value}
          disabled={disabled}
          checked={selfChecked}
          invalid={invalid}
          aria-checked={selfChecked}
          aria-disabled={disabled}
          aria-invalid={invalid}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...rest}
        />
        {labelElement && labelPosition === 'left' && labelElement}
        <Track
          className='mote-switch__track'
          color={color}
          aria-hidden={true}
          data-checked={dataAttr(selfChecked)}
          data-active={dataAttr(isActive)}
          data-focus={dataAttr(isFocused)}
          data-disabled={dataAttr(disabled)}
          data-invalid={dataAttr(invalid)}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
        >
          <Thumb
            className='mote-switch__thumb'
            data-checked={dataAttr(selfChecked)}
          >
            {circleIcon}
          </Thumb>
        </Track>
        {labelElement && labelPosition === 'right' && labelElement}
      </Root>
    )
  }
)

if (__DEV__) {
  Switch.displayName = 'Switch'
}

const SwitchInput = styled.input`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
  word-wrap: normal;

  &:focus[data-focus-visible-added] + span[data-focus] {
    box-shadow: outline-primary;
  }
`

const Thumb = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  transition-property: transform, width;
  transition-duration: 150ms;
  border-radius: inherit;
  width: var(--switch-track-height);
  height: var(--switch-track-height);

  svg {
    width: calc(var(--switch-track-height) * 0.44);
    height: calc(var(--switch-track-height) * 0.44);
  }

  &[data-checked] {
    transform: translateX(var(--switch-thumb-x));
  }
`

const Root = styled.label`
  ${({ size }) => css`
    --switch-track-width: ${SIZES[size].switch.width}px;
    --switch-track-height: ${SIZES[size].switch.height}px;
  `};

  --switch-track-diff: calc(
    var(--switch-track-width) - var(--switch-track-height)
  );
  --switch-thumb-x: var(--switch-track-diff);

  display: inline-flex;
  justify-content: flex-start;
  align-items: center;
  vertical-align: top;
  position: relative;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  gap: ${({ gap }) => (isNumber(gap) ? `${gap}px` : gap)};

  &:active {
    & ${Thumb} {
      width: calc(var(--switch-track-height) + (var(--switch-track-width) / 8));
      transform: translateX(calc(var(--switch-track-width) / 16));

      &[data-checked] {
        transform: translateX(
          calc(
            var(--switch-thumb-x) - (var(--switch-track-width) / 8) -
              (var(--switch-track-width) / 16)
          )
        );
      }
    }
  }

  &[data-disabled] {
    cursor: not-allowed;
  }
`

const Track = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  justify-content: flex-start;
  box-sizing: content-box;
  cursor: pointer;
  border-radius: var(--switch-track-height);
  padding: 2px;
  width: var(--switch-track-width);
  height: var(--switch-track-height);
  background: ${th.color('grey.l3')};
  transition-duration: 150ms;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &:focus,
  &[data-focus] {
    box-shadow: outline-primary;
  }

  &[data-disabled] {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &[data-checked] {
    ${({ color }) =>
      css`
        background: ${th.color(color)};
      `};
  }
`

const Label = styled.span`
  display: inline-block;
  font-size: ${({ size }) => SIZES[size].font}px;
  line-height: 120%;
  font-style: normal;
  font-weight: 400;
  color: black;
  user-select: none;
  margin-top: 1px;
`
